import React, { useState } from 'react';
import { Search, Heart, ShoppingCart, User, Menu, X, Star, ChevronDown, LayoutDashboard, LogOut } from 'lucide-react';
import EnhancedSearch from './EnhancedSearch';
import ThemeToggle from './ThemeToggle';
import LanguageCurrencySelector from './LanguageCurrencySelector';
import NotificationsPanel from './NotificationsPanel';
import MegaCategoryDropdown from './MegaCategoryDropdown';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

const Header = ({ cartCount = 0, wishlistCount = 0, onCartClick, onAuthClick, onQuickView, onCategorySelect, onViewAllProducts, user, categories = [], onSearch }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { isAuthenticated, isAdmin, isAffiliate, logout } = useAuth();
  const settings = useSettings();

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-40" role="banner">
      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-xl" aria-label={`${settings.site_name} Home`}>
            {settings.site_logo ? (
              <img src={settings.site_logo} alt={settings.site_name} className="w-12 h-12 rounded-xl object-contain" />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">{settings.logo_text || 'M+'}</span>
              </div>
            )}
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-gray-800 dark:text-white">{settings.site_name}</h1>
              <p className="text-xs text-teal-600 dark:text-teal-400">{settings.site_tagline}</p>
            </div>
          </a>

          {/* Search Bar with Autocomplete */}
          <div className="hidden md:block flex-1 max-w-2xl">
            <EnhancedSearch onProductSelect={onQuickView} onSearch={onSearch} onCategorySelect={onCategorySelect} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language/Currency Selector */}
            <div className="hidden sm:block">
              <LanguageCurrencySelector user={user} />
            </div>

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Notifications (for logged-in users) */}
            {isAuthenticated && user?.id && (
              <div className="hidden sm:block">
                <NotificationsPanel userId={user.id} />
              </div>
            )}

            {/* Wishlist */}
            <button 
              className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors hidden sm:block focus:outline-none focus:ring-2 focus:ring-teal-500"
              aria-label={`Wishlist with ${wishlistCount} items`}
            >
              <Heart className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            {/* Cart */}
            <button
              onClick={onCartClick}
              className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              aria-label={`Shopping cart with ${cartCount} items`}
            >
              <ShoppingCart className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-600 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {cartCount}
                </span>
              )}
              <span className="hidden lg:inline text-sm text-gray-600 dark:text-gray-400">My Cart ({cartCount})</span>
            </button>

            {/* User Menu / Sign In */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="hidden sm:flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <img src={user?.avatar} alt="" className="w-8 h-8 rounded-full" />
                  <span className="text-sm font-medium max-w-24 truncate">{user?.name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50">
                    <div className="p-4 border-b dark:border-gray-700">
                      <p className="font-medium text-gray-800 dark:text-white">{user?.name}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                      <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                        isAdmin ? 'bg-amber-100 text-amber-700' : 
                        isAffiliate ? 'bg-purple-100 text-purple-700' : 
                        'bg-teal-100 text-teal-700'
                      }`}>
                        {isAdmin ? 'Administrator' : isAffiliate ? 'Affiliate Partner' : 'Customer'}
                      </span>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => {
                          onAuthClick();
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        <span>{isAdmin ? 'Admin Dashboard' : isAffiliate ? 'Affiliate Center' : 'My Account'}</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={onAuthClick}
                className="hidden sm:flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
                aria-haspopup="dialog"
              >
                <User className="w-5 h-5" />
                <span className="text-sm font-medium">Sign In</span>
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors md:hidden focus:outline-none focus:ring-2 focus:ring-teal-500"
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700 dark:text-gray-300" /> : <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="mt-3 md:hidden">
          <EnhancedSearch onProductSelect={onQuickView} onSearch={onSearch} onCategorySelect={onCategorySelect} />
        </div>
      </div>

      {/* Rating & Categories Bar */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          {/* Rating Badge */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="text-2xl font-bold text-gray-800 dark:text-white">4.8</span>
              <div className="flex" role="img" aria-label="Rated 4.8 out of 5 stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= 4
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                  />
                ))}
              </div>
              <span className="text-gray-500 dark:text-gray-400 text-sm">(2.5k+ reviews)</span>
            </div>
            <button className="px-3 py-1 bg-teal-600 text-white text-xs font-medium rounded-full hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400">
              REVIEWS
            </button>
          </div>

          {/* Categories Navigation */}
          <nav className="hidden lg:flex items-center gap-1 relative" aria-label="Product categories">
            {/* Mega Category Dropdown */}
            <MegaCategoryDropdown onCategorySelect={onCategorySelect} onViewAll={onViewAllProducts} />

            {/* Quick Links - Show first 4 real categories */}
            {categories.slice(0, 4).map((category) => (
              <button
                key={category.id}
                onClick={() => onCategorySelect?.(category.id)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid={`header-category-${category.slug}`}
              >
                {category.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t dark:border-gray-800" role="navigation">
          <nav className="p-4 space-y-2" aria-label="Mobile navigation">
            <button
              onClick={() => {
                onCategorySelect?.('all');
                setMobileMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-600 dark:hover:text-teal-400 rounded-lg transition-colors font-medium"
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  onCategorySelect?.(category.id);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-600 dark:hover:text-teal-400 rounded-lg transition-colors"
                data-testid={`mobile-category-${category.slug}`}
              >
                {category.name}
              </button>
            ))}
            <hr className="my-2 dark:border-gray-700" />
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => {
                    onAuthClick();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>{isAdmin ? 'Admin Dashboard' : 'My Account'}</span>
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  onAuthClick();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 w-full px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg"
              >
                <User className="w-5 h-5" />
                <span>Sign In / Register</span>
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
